import { Form, Formik, type FormikHelpers } from "formik";
import { object, string } from "yup";
import { useState } from "react";
import { IconTrash } from "@tabler/icons-react";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select.jsx";
import IconButton from "../UI/IconButton";
import CountBadge from "../UI/CountBadge";
// @ts-expect-error pure js
import ToggleSwitch from "../UI/ToggleSwitch";
import ModalDialog from "../UI/ModalDialog";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import {
  EMAIL_INVALID_MESSAGE,
  EMAIL_REGEX,
} from "../../utils/constants/validation";
import styles from "./ProfileNomShareForm.module.css";
import type { Droits, SharedCompte } from "./types";

interface ShareFormValues {
  mail: string;
  droits: Droits;
}

interface DroitsOption {
  value: Droits;
  label: string;
}

const DROITS_OPTIONS: DroitsOption[] = [
  { value: "RW", label: "Modifier" },
  { value: "RO", label: "Lecture seule" },
];

interface ProfileNomShareFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sharedComptes: SharedCompte[];
  onAddShare: (
    values: ShareFormValues,
    helpers: FormikHelpers<ShareFormValues>,
  ) => void | Promise<void>;
  onUpdateDroits: (mail: string, droits: Droits) => Promise<void>;
  onDeleteShare: (mail: string) => Promise<void>;
}

interface SharedCompteRowProps {
  compte: SharedCompte;
  onChangeDroits: (droits: Droits) => Promise<void>;
  onDelete: () => Promise<void>;
}

function SharedCompteRow({
  compte,
  onChangeDroits,
  onDelete,
}: SharedCompteRowProps) {
  async function handleDelete() {
    if (
      window.confirm(
        `Voulez-vous vraiment retirer le partage avec ${compte.mail} ?`,
      )
    ) {
      await onDelete();
    }
  }

  async function handleToggle() {
    try {
      await onChangeDroits(compte.droits === "RW" ? "RO" : "RW");
    } catch (error) {
      handleAPIError(error);
    }
  }

  return (
    <>
      <Text
        as="span"
        weight="medium"
        className={styles.shareMail}
        title={compte.mail}
      >
        {compte.mail}
      </Text>
      <Text as="span" variant="deci" color="muted">
        Autoriser les modifications
      </Text>
      <ToggleSwitch
        status={compte.droits === "RW"}
        onToggle={handleToggle}
        requiresWrite
      />
      <IconButton
        icon={IconTrash}
        label="Retirer le partage"
        onClick={handleDelete}
        color="danger"
        requiresWrite
      />
    </>
  );
}

export default function ProfileNomShareForm({
  open,
  onOpenChange,
  sharedComptes,
  onAddShare,
  onUpdateDroits,
  onDeleteShare,
}: ProfileNomShareFormProps) {
  const [error, setError] = useState("");

  return (
    <Formik<ShareFormValues>
      initialValues={{ mail: "", droits: "RW" }}
      enableReinitialize
      onSubmit={async (values, helpers) => {
        setError("");
        try {
          await onAddShare(values, helpers);
        } catch (e) {
          setError(handleAPIError(e));
        }
      }}
      validationSchema={object({
        mail: string()
          .matches(EMAIL_REGEX, EMAIL_INVALID_MESSAGE)
          .required("L'adresse e-mail est obligatoire"),
      })}
    >
      {({ resetForm }) => (
        <ModalDialog
          size="md"
          open={open}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              setError("");
            }
            onOpenChange(nextOpen);
          }}
          header="Partager votre profil bailleur"
          subtitle="Invitez un associé ou un gestionnaire et choisissez ce qu'il peut faire."
        >
          <div className={styles.body}>
            <Form className={classnames(styles.panel, styles.invitePanel)}>
              <div className={styles.inviteRow}>
                <TextInput
                  type="email"
                  name="mail"
                  label="Adresse de la personne à inviter"
                  placeholder="contact@qalimo.fr"
                  marginForm=""
                />
                <FormikSubmitButton requiresWrite>Inviter</FormikSubmitButton>
              </div>
              <ReactSelect
                label="Sélectionnez les droits à accorder"
                name="droits"
                options={DROITS_OPTIONS}
                isClearable={false}
                isSearchable={false}
              />
              <Text variant="deci" color="muted">
                Un accès Qalimo est créé si la personne n'en a pas.
              </Text>
              <Callout variant="danger">{error}</Callout>
            </Form>

            <Text
              as="aside"
              color="info"
              background
              className={classnames(styles.panel, styles.bankNotice)}
            >
              <Text as="span" variant="deci" weight="bold">
                Les comptes bancaires ne sont pas partagés automatiquement.
              </Text>
              <Text as="span" variant="deci">
                Associez-les depuis Finance → Mes banques.
              </Text>
            </Text>

            {sharedComptes.length > 0 && (
              <section className={styles.shareSection}>
                <header className={styles.shareSectionHeader}>
                  <Text
                    as="h3"
                    variant="deci"
                    weight="bold"
                    color="muted"
                    className={styles.shareSectionTitle}
                  >
                    Personnes ayant accès
                  </Text>
                  <CountBadge count={sharedComptes.length} />
                </header>
                <ul className={styles.shareList}>
                  {sharedComptes.map((compte) => (
                    <li key={compte.mail} className={styles.shareRow}>
                      <SharedCompteRow
                        compte={compte}
                        onChangeDroits={(droits) =>
                          onUpdateDroits(compte.mail, droits)
                        }
                        onDelete={() => onDeleteShare(compte.mail)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </ModalDialog>
      )}
    </Formik>
  );
}
